/**
 * Redux slice for the new ScrapeJob system.
 *
 * State of the world:
 *   byId          — { [job_id]: ScrapeJob }
 *   idsByPolicy   — { [policy_id]: [job_id, ...] }
 *   idsByPatient  — { [patient_id]: [job_id, ...] }
 *   twoFAQueue    — [job_id, ...] in awaiting_2fa, FIFO
 *   wsConnected   — bool
 *   lastSyncAt    — ISO timestamp of the last successful BULK_SYNC
 *
 * Action shape: { type, job?, jobs?, jobId? }
 *
 * The selectors at the bottom of this file are the only thing UI surfaces
 * should import. Components must not reach into byId directly.
 */

export const SCRAPE_JOB_UPSERT = 'scrapeJobs/UPSERT'
export const SCRAPE_JOB_REMOVED = 'scrapeJobs/REMOVED'
export const SCRAPE_JOBS_BULK_SYNC = 'scrapeJobs/BULK_SYNC'
export const SCRAPE_JOBS_WS_CONNECTED = 'scrapeJobs/WS_CONNECTED'
export const SCRAPE_JOBS_WS_DISCONNECTED = 'scrapeJobs/WS_DISCONNECTED'
export const SCRAPE_JOBS_SET_OFFICE = 'scrapeJobs/SET_OFFICE'

const ACTIVE_STATES = new Set(['queued', 'claimed', 'running', 'awaiting_2fa'])
const TERMINAL_STATES = new Set(['succeeded', 'failed', 'cancelled', 'timed_out'])

const initialState = {
  byId: {},
  idsByPolicy: {},
  idsByPatient: {},
  twoFAQueue: [],
  wsConnected: false,
  lastSyncAt: null,
  // The office these jobs belong to. Surfaces scope to it so one office never
  // shows another's runs. Set by the socket manager from the selected office.
  officeId: null
}

function normalizeJob (job) {
  // Server payload arrives with both job_id and id (depending on shape).
  return {
    job_id: job.job_id || job.id,
    state: job.state,
    office_id: job.office_id,
    patient_id: job.patient_id || null,
    patient_name: job.patient_name || '',
    policy_id: job.policy_id || null,
    carrier_code: job.carrier_code || '',
    operation_type: job.operation_type || '',
    progress_label: job.progress_label || null,
    progress_pct: job.progress_pct == null ? null : Number(job.progress_pct),
    twofa_required: !!job.twofa_required,
    twofa_required_at: job.twofa_required_at || null,
    twofa_received: !!job.twofa_received,
    pending_user_input_token: job.pending_user_input_token || null,
    result_breakdown_id: job.result_breakdown_id || null,
    result_payload: job.result_payload || null,
    error_code: job.error_code || null,
    error_message: job.error_message || null,
    created_at: job.created_at || null,
    updated_at: job.updated_at || null,
    started_at: job.started_at || null,
    completed_at: job.completed_at || null
  }
}

function addToIndex (index, key, jobId) {
  if (key == null) return index
  const list = index[key] ? [...index[key]] : []
  if (!list.includes(jobId)) list.push(jobId)
  return { ...index, [key]: list }
}

function removeFromIndex (index, key, jobId) {
  if (key == null || !index[key]) return index
  const list = index[key].filter(function (id) { return id !== jobId })
  const next = { ...index }
  if (list.length) next[key] = list
  else delete next[key]
  return next
}

function recomputeTwoFAQueue (byId) {
  // FIFO — oldest twofa_required_at first.
  const ids = Object.keys(byId).filter(function (id) {
    return byId[id].state === 'awaiting_2fa' && !byId[id].twofa_received
  })
  ids.sort(function (a, b) {
    const ta = byId[a].twofa_required_at || byId[a].created_at || ''
    const tb = byId[b].twofa_required_at || byId[b].created_at || ''
    return ta.localeCompare(tb)
  })
  return ids
}

function applyUpsert (state, rawJob) {
  if (!rawJob) return state
  const job = normalizeJob(rawJob)
  if (!job.job_id) return state

  const prev = state.byId[job.job_id]

  // Maintain indexes if patient/policy changed (rare but possible).
  let idsByPolicy = state.idsByPolicy
  let idsByPatient = state.idsByPatient
  if (prev) {
    if (prev.policy_id !== job.policy_id) {
      idsByPolicy = removeFromIndex(idsByPolicy, prev.policy_id, job.job_id)
    }
    if (prev.patient_id !== job.patient_id) {
      idsByPatient = removeFromIndex(idsByPatient, prev.patient_id, job.job_id)
    }
  }
  idsByPolicy = addToIndex(idsByPolicy, job.policy_id, job.job_id)
  idsByPatient = addToIndex(idsByPatient, job.patient_id, job.job_id)

  const byId = { ...state.byId, [job.job_id]: job }
  return {
    ...state,
    byId,
    idsByPolicy,
    idsByPatient,
    twoFAQueue: recomputeTwoFAQueue(byId)
  }
}

export function scrapeJobs (state = initialState, action) {
  switch (action.type) {
    case SCRAPE_JOB_UPSERT:
      return applyUpsert(state, action.job)

    case SCRAPE_JOB_REMOVED: {
      if (!action.jobId || !state.byId[action.jobId]) return state
      const prev = state.byId[action.jobId]
      const byId = { ...state.byId }
      delete byId[action.jobId]
      return {
        ...state,
        byId,
        idsByPolicy: removeFromIndex(state.idsByPolicy, prev.policy_id, action.jobId),
        idsByPatient: removeFromIndex(state.idsByPatient, prev.patient_id, action.jobId),
        twoFAQueue: recomputeTwoFAQueue(byId)
      }
    }

    case SCRAPE_JOBS_BULK_SYNC: {
      const incoming = Array.isArray(action.jobs) ? action.jobs : []
      let next = { ...state, lastSyncAt: action.lastSyncAt || new Date().toISOString() }
      // Merge — UPSERT semantics. We don't drop existing entries that
      // weren't in the snapshot, since the snapshot is filtered by state.
      for (let i = 0; i < incoming.length; i++) {
        next = applyUpsert(next, incoming[i])
      }
      return next
    }

    case SCRAPE_JOBS_SET_OFFICE: {
      if (action.officeId === state.officeId) return state
      // Office changed → drop the previous office's jobs so every surface stays
      // scoped to the selected office. The new office's WS bulk-syncs fresh
      // (lastSyncAt resets to null, forcing a full active-jobs sync).
      return { ...initialState, officeId: action.officeId, wsConnected: state.wsConnected }
    }

    case SCRAPE_JOBS_WS_CONNECTED:
      return { ...state, wsConnected: true }

    case SCRAPE_JOBS_WS_DISCONNECTED:
      return { ...state, wsConnected: false }

    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Selectors — UI surfaces should import only these.
// ---------------------------------------------------------------------------

export function selectAllJobs (state) {
  const s = state.scrapeJobs
  const cur = s.officeId
  const byId = s.byId
  return Object.keys(byId)
    .map(function (id) { return byId[id] })
    .filter(function (j) {
      // Scope to the selected office (defense-in-depth on top of the
      // reset-on-office-change reducer case). Jobs with no office_id are kept.
      return cur == null || j.office_id == null || String(j.office_id) === String(cur)
    })
}

export function selectActiveJobs (state) {
  return selectAllJobs(state).filter(function (j) { return ACTIVE_STATES.has(j.state) })
}

export function selectActiveJobsForPatient (state, patientId) {
  if (patientId == null) return []
  const ids = state.scrapeJobs.idsByPatient[patientId] || []
  return ids
    .map(function (id) { return state.scrapeJobs.byId[id] })
    .filter(function (j) { return j && ACTIVE_STATES.has(j.state) })
}

export function selectActiveJobsForPolicy (state, policyId) {
  if (policyId == null) return []
  const ids = state.scrapeJobs.idsByPolicy[policyId] || []
  return ids
    .map(function (id) { return state.scrapeJobs.byId[id] })
    .filter(function (j) { return j && ACTIVE_STATES.has(j.state) })
}

export function selectRecentCompletedJobs (state, limit) {
  const cap = limit || 20
  return selectAllJobs(state)
    .filter(function (j) { return TERMINAL_STATES.has(j.state) })
    .sort(function (a, b) {
      const ta = a.completed_at || a.updated_at || ''
      const tb = b.completed_at || b.updated_at || ''
      return tb.localeCompare(ta)
    })
    .slice(0, cap)
}

// Failure-shape states are a strict subset of TERMINAL_STATES — we
// intentionally exclude `cancelled` here because cancellation is a
// user-intentional action that shouldn't show as a failure surface.
const FAILED_STATES = new Set(['failed', 'timed_out'])

function _toMs (iso) {
  if (!iso) return 0
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? 0 : t
}

/** Most-recent job in a failure-shape state for a given patient. Returns
 *  the single newest such job (by completed_at), or null. Returned shape
 *  matches the dicts in `state.scrapeJobs.byId`. */
export function selectLatestFailedJobForPatient (state, patientId) {
  if (patientId == null) return null
  const ids = state.scrapeJobs.idsByPatient[patientId] || []
  let best = null
  for (let i = 0; i < ids.length; i++) {
    const j = state.scrapeJobs.byId[ids[i]]
    if (!j || !FAILED_STATES.has(j.state)) continue
    if (!best || _toMs(j.completed_at || j.updated_at) > _toMs(best.completed_at || best.updated_at)) {
      best = j
    }
  }
  return best
}

/** Most-recent job in a failure-shape state for a given policy. Mirrors
 *  `selectLatestFailedJobForPatient` but indexed by policy_id, so the
 *  Insurance Breakdown modal can scope its failure card to whichever
 *  policy the user is currently viewing. */
export function selectLatestFailedJobForPolicy (state, policyId) {
  if (policyId == null) return null
  const ids = state.scrapeJobs.idsByPolicy[policyId] || []
  let best = null
  for (let i = 0; i < ids.length; i++) {
    const j = state.scrapeJobs.byId[ids[i]]
    if (!j || !FAILED_STATES.has(j.state)) continue
    if (!best || _toMs(j.completed_at || j.updated_at) > _toMs(best.completed_at || best.updated_at)) {
      best = j
    }
  }
  return best
}

export function selectAwaitingTwoFAJobs (state) {
  return state.scrapeJobs.twoFAQueue
    .map(function (id) { return state.scrapeJobs.byId[id] })
    .filter(Boolean)
}

export function selectTwoFAQueueHead (state) {
  const head = state.scrapeJobs.twoFAQueue[0]
  return head ? state.scrapeJobs.byId[head] : null
}

export function selectWsConnected (state) {
  return !!state.scrapeJobs.wsConnected
}
